import { Link, Redirect, useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { Menu } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DnaLogo } from "@/components/DnaLogo";
import { isStaffPreviewMode, isStaffUser } from "@/lib/staff";
import { cn } from "@/lib/utils";

function BrandMark({ compact = false, onClick }: { compact?: boolean; onClick?: () => void }) {
  return (
    <Link href="/" className="flex items-center gap-3 group" aria-label="MOTUS Bug Board home" onClick={onClick}>
      <div className="flex flex-col gap-1 leading-none">
        <span
          className="font-[family-name:var(--dna-font-display)] text-[15px] font-bold leading-tight text-[var(--dna-ink)] sm:text-[17px]"
        >
          MOTUS Bug Board
        </span>
        <DnaLogo
          className={compact ? "h-5 w-auto" : "h-5 w-auto sm:h-6"}
          width={compact ? 96 : 115}
          height={compact ? 20 : 24}
        />
      </div>
    </Link>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isLoaded, isSignedIn, user } = useUser();
  const isHome = location === "/";

  if (!isLoaded) {
    return <div className="min-h-screen bg-[var(--dna-bg)]" />;
  }

  if (isSignedIn && isStaffUser(user) && !isStaffPreviewMode()) {
    return <Redirect replace to="/staff" />;
  }

  const navClass = (active: boolean) =>
    cn(
      "text-sm font-medium transition-colors",
      active
        ? "text-[var(--dna-indigo-700)] font-semibold"
        : "text-[var(--dna-slate-600)] hover:text-[var(--dna-indigo-800)]",
    );

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`flex ${mobile ? "flex-col space-y-4" : "items-center gap-7"}`}>
      <Link
        href="/"
        className={navClass(location === "/")}
        onClick={() => mobile && setIsMobileMenuOpen(false)}
      >
        <span data-testid="nav-public-board">Known issues</span>
      </Link>
      <Link
        href="/whats-changed"
        className={navClass(location === "/whats-changed")}
        onClick={() => mobile && setIsMobileMenuOpen(false)}
      >
        <span data-testid="nav-whats-changed">What's changed</span>
      </Link>
      {/* Server-rendered page, so a plain anchor: it is not a route here. */}
      <a href="/faq" className={navClass(false)} data-testid="nav-faq">
        FAQ
      </a>
      <Link
        href="/my-reports"
        className={navClass(location === "/my-reports")}
        onClick={() => mobile && setIsMobileMenuOpen(false)}
      >
        <span data-testid="nav-my-reports">Track my reports</span>
      </Link>
    </div>
  );

  return (
    <div className="dna-public min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 w-full border-b border-[var(--dna-slate-200)] bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="mx-auto flex min-h-16 max-w-[var(--dna-maxw)] items-center justify-between gap-3 px-4 py-2 md:h-16 md:px-10 md:py-0">
          <BrandMark />

          <nav className="hidden items-center gap-7 md:flex">
            <NavLinks />
            <Link
              href="/report"
              className="dna-btn-primary"
              data-testid="nav-submit-report"
            >
              Report a bug
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:hidden">
            <Link href="/report" className="dna-btn-primary">
              Report a bug
            </Link>
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-[var(--dna-radius-sm)] text-[var(--dna-slate-600)] hover:bg-[var(--dna-slate-100)]"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Toggle Menu</span>
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white sm:w-[340px]">
                <div className="mt-6 flex flex-col gap-8">
                  <BrandMark compact onClick={() => setIsMobileMenuOpen(false)} />
                  <NavLinks mobile />
                  <Link
                    href="/report"
                    className="dna-btn-primary dna-btn-primary-block"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Report a bug
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main
        className={
          isHome ? "flex-1" : "mx-auto w-full max-w-[var(--dna-maxw)] flex-1 px-4 py-6 md:px-10 md:py-8"
        }
      >
        {children}
      </main>

      <footer className="mt-auto border-t border-[var(--dna-slate-200)] bg-white py-6 md:py-8">
        <div className="mx-auto flex max-w-[var(--dna-maxw)] flex-col items-center justify-between gap-4 px-4 text-center md:flex-row md:px-10 md:text-left">
          <div className="flex flex-col items-center gap-2 md:items-start">
            <a
              href="https://donnormanassociates.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DnaLogo className="h-5 w-auto" width={96} height={20} loading="lazy" />
            </a>
            <p className="text-sm text-[var(--dna-slate-500)]">
              MOTUS Bug Board &copy; 2026. Not affiliated with the FMCSA.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link href="/" className="dna-link text-sm text-[var(--dna-slate-500)]">Issues</Link>
            <Link href="/report" className="dna-link text-sm text-[var(--dna-slate-500)]">Report a bug</Link>
            <Link href="/whats-changed" className="dna-link text-sm text-[var(--dna-slate-500)]">What's changed</Link>
            <a href="/issues" className="dna-link text-sm text-[var(--dna-slate-500)]">All issues</a>
            <a href="/guides/motus-login-errors" className="dna-link text-sm text-[var(--dna-slate-500)]">Login errors</a>
            <a href="/faq" className="dna-link text-sm text-[var(--dna-slate-500)]">FAQ</a>
            <a href="/about" className="dna-link text-sm text-[var(--dna-slate-500)]">About</a>
            <Link href="/terms" className="dna-link text-sm text-[var(--dna-slate-500)]">Terms of Service</Link>
            <Link href="/privacy" className="dna-link text-sm text-[var(--dna-slate-500)]">Privacy Policy</Link>
            <Link href="/staff" className="text-sm text-[var(--dna-slate-400)] hover:text-[var(--dna-slate-600)]" data-testid="footer-staff-link">Staff</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
